// i18n (Internationalization) utility functions
// Handles translation, language switching, and RTL support

// Import translations and metadata from translations module
import { translations, languageMetadata } from './translations.js';
import { getCurrentTenant } from './tenant.js';

const i18n = {
    currentLanguage: 'en', // Default language (changed from 'de')

    // Initialize i18n system
    init() {
        // Load saved language from localStorage or use default
        const savedLang = localStorage.getItem('appLanguage');
        const tenant = getCurrentTenant();

        // Validate saved language against current tenant's allowed languages
        if (savedLang && this.isLanguageAvailable(savedLang)) {
            this.currentLanguage = savedLang;
        } else if (savedLang && !this.isLanguageAvailable(savedLang)) {
            // Language exists but not available for this tenant - reset to tenant default
            console.log(`Language ${savedLang} not available for ${tenant.name} tenant. Using default: ${tenant.defaultLanguage}`);
            this.currentLanguage = tenant.defaultLanguage;
        } else {
            // No saved language - use tenant default
            this.currentLanguage = tenant.defaultLanguage;
        }

        // Apply initial language
        this.applyLanguage(this.currentLanguage);
    },

    // Get current language
    getCurrentLanguage() {
        return this.currentLanguage;
    },

    // Set and persist language
    setLanguage(lang) {
        if (!translations[lang]) {
            console.error(`Language ${lang} not found`);
            return false;
        }

        this.currentLanguage = lang;
        localStorage.setItem('appLanguage', lang);
        this.applyLanguage(lang);

        // Dispatch language change event
        window.dispatchEvent(new CustomEvent('languageChanged', {
            detail: { language: lang }
        }));

        return true;
    },

    // Translate a key with support for nested keys and variable substitution.
    // Falls back to English if the key is missing in the current language.
    t(key, variables = {}) {
        const keys = key.split('.');

        const resolve = (lang) => {
            let value = translations[lang];
            for (const k of keys) {
                if (value && typeof value === 'object') {
                    value = value[k];
                } else {
                    return undefined;
                }
            }
            return value;
        };

        let value = resolve(this.currentLanguage);
        if (value === undefined && this.currentLanguage !== 'en') {
            value = resolve('en');
        }
        if (value === undefined) {
            console.warn(`Translation key not found: ${key}`);
            return key;
        }

        // Handle variable substitution (e.g., {name}, {model}, {api})
        if (typeof value === 'string' && Object.keys(variables).length > 0) {
            return value.replace(/\{(\w+)\}/g, (match, varName) => {
                return variables[varName] !== undefined ? variables[varName] : match;
            });
        }

        return value || key;
    },

    // Apply language to all elements with data-i18n attributes
    applyLanguage(lang) {
        const metadata = languageMetadata[lang];
        if (!metadata) return;

        // Update HTML lang and dir attributes
        document.documentElement.lang = lang;
        document.documentElement.dir = metadata.dir;

        // Apply RTL class for styling
        if (metadata.dir === 'rtl') {
            document.body.classList.add('rtl');
        } else {
            document.body.classList.remove('rtl');
        }

        // Translate all elements with data-i18n attribute
        this.translateElements();
    },

    // Translate all DOM elements with data-i18n attributes
    translateElements() {
        // Translate text content
        document.querySelectorAll('[data-i18n]').forEach(el => {
            const key = el.getAttribute('data-i18n');
            el.textContent = this.t(key);
        });

        // Translate placeholders
        document.querySelectorAll('[data-i18n-placeholder]').forEach(el => {
            const key = el.getAttribute('data-i18n-placeholder');
            el.placeholder = this.t(key);
        });

        // Translate titles/tooltips
        document.querySelectorAll('[data-i18n-title]').forEach(el => {
            const key = el.getAttribute('data-i18n-title');
            el.title = this.t(key);
        });

        // Translate aria-labels
        document.querySelectorAll('[data-i18n-aria]').forEach(el => {
            const key = el.getAttribute('data-i18n-aria');
            el.setAttribute('aria-label', this.t(key));
        });
    },

    // Get language metadata
    getLanguageMetadata(lang) {
        return languageMetadata[lang] || null;
    },

    // Get all available languages (unfiltered - for backward compatibility)
    getAvailableLanguages() {
        const tenant = getCurrentTenant();
        return this.getAvailableLanguagesByTenant(tenant.languages);
    },

    // Get available languages filtered by tenant
    getAvailableLanguagesByTenant(tenantLanguages) {
        return tenantLanguages.map(lang => ({
            code: lang,
            ...languageMetadata[lang]
        })).filter(lang => lang && lang.name);
    },

    // Check if a language is available for the current tenant
    isLanguageAvailable(langCode) {
        const tenant = getCurrentTenant();
        return tenant.languages.includes(langCode);
    },

    // Check if current language is RTL
    isRTL() {
        return languageMetadata[this.currentLanguage]?.dir === 'rtl';
    }
};

// Make i18n available globally
window.i18n = i18n;

// Initialize on DOM ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => i18n.init());
} else {
    i18n.init();
}

export default i18n;
